import { isNullOrUndefined, mobile } from '@bimser/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import DataGrid from '../..';
import { IControlProperties } from '../../../../../../entities';
import { IControlDataGridColumn, IDataGridCell, IDataGridRow } from '../../entities';
import _merge from 'lodash/merge';

dayjs.extend(localeData);

export function getNewRow(
	this: DataGrid,
	row?: any,
	oldRow?: IDataGridRow,
	useOldRowAsPrimaryKey: boolean = false
) {
	const newRow = this.newRow(oldRow);
	const oldRowData =
		oldRow?.cells?.reduce(
			(result, current) => ({ ...result, [current.name]: current.value }),
			{}
		) || {};

	if (!useOldRowAsPrimaryKey) {
		const primaryKey = this.columns.find(col => col.isPrimaryKey);
		if (primaryKey && primaryKey.autoIncrement) {
			const primaryKeyValue = newRow.cells?.find(cell => cell.name === primaryKey.name)
				?.value;

			if (row && !isNullOrUndefined(primaryKeyValue)) {
				if (row[primaryKey.name]) {
					row[primaryKey.name] = Math.max(row[primaryKey.name], primaryKeyValue);
				} else {
					row[primaryKey.name] = primaryKeyValue;
				}
			}
		}
	}

	const newRowData = _merge(oldRowData, row);

	const value = [...this.rows]?.sort((a, b) => b.index - a.index)?.find(v => v) || null;

	let index!: number;
	if (oldRow) {
		index = oldRow.index;
	} else {
		if (value) {
			index = Number(value.index) + 1;
		} else {
			index = newRow.index;
		}
	}

	Object.keys(row).forEach(columnName => {
		const column = this.columns.find(col => col.name === columnName);
		newRow.cells = newRow.cells.map(cell => getNewCell(column, row, columnName, cell));
		if (columnName == 'index') {
			index = row[columnName];
		}
	});
	newRow.index = index;
	newRow.data = Object.keys(newRowData)
		.filter(key => !key.includes('__'))
		.reduce(
			(obj, key) =>
				Object.assign(obj, {
					[key]: newRowData[key]
				}),
			{}
		);

	newRow.cellData = Object.keys(newRowData)
		.filter(key => key.includes('__'))
		.reduce(
			(obj, key) =>
				Object.assign(obj, {
					[key]: newRowData[key]
				}),
			{}
		);
	return newRow;
}

function getNewCell(
	column: IControlDataGridColumn<IControlProperties>,
	row: any,
	columnName: string,
	cell: IDataGridCell
) {
	if (cell.name === columnName) {
		cell.value = row[columnName];
		switch (cell.editType) {
			case 'comboBox': {
				cell.text = !isNullOrUndefined(row[columnName]?.text) ? row[columnName].text : null;
				if (!isNullOrUndefined(row[columnName]?.value)) {
					cell.value = row[columnName].value;
				} else if (typeof row[columnName] !== 'object') {
					cell.value = row[columnName];
					if (column.dbType.type !== 'object') cell.text = row[columnName];
				} else {
					cell.value = null;
				}
				break;
			}
			case 'textBox': {
				cell.text = row[columnName];
				cell.value = row[columnName];
				break;
			}
			case 'datePicker': {
				if (mobile().any()) {
					cell.value = !isNullOrUndefined(cell.value) ? dayjs(cell.value).format() : null;
				} else {
					cell.value = !isNullOrUndefined(cell.value)
						? dayjs(cell.value).format('YYYY-MM-DD')
						: null;
				}
				break;
			}
			case 'dateTimePicker': {
				cell.value = !isNullOrUndefined(cell.value) ? dayjs(cell.value).format() : null;
				break;
			}
			case 'timePicker': {
				if (!isNullOrUndefined(cell.value)) {
					if (dayjs(cell.value)?.isValid()) {
						cell.value = dayjs(cell.value).format();
					}
				} else {
					cell.value = null;
				}
				break;
			}
			// case "multiLanguageTextBox": {
			//     cell.text = typeof cell.value == "object" ? cell.value?.[this.__form.currentLanguage] : cell.value;
			// }
		}
	}
	return cell;
}

export function compareDeletingRowByCellType(value, compareValue, dataType, format) {
	switch (dataType) {
		case 'dateTime':
			const datetimeDay = dayjs(value).isSame(dayjs(compareValue), 'day');
			const datetimeHour = dayjs(value).isSame(dayjs(compareValue), 'hour');
			const datetimeMinute = dayjs(value).isSame(dayjs(compareValue), 'minute');
			return !(datetimeDay && datetimeHour && datetimeMinute);
		case 'date':
			return !dayjs(value).isSame(compareValue, 'day');
		case 'time':
			const hour = dayjs(value).isSame(dayjs(compareValue), 'hour');
			const minute = dayjs(value).isSame(dayjs(compareValue), 'minute');
			let second = dayjs(value).isSame(dayjs(compareValue), 'second');
			if (format === dayjs().localeData().longDateFormat('LTS')) {
				second = true;
			}
			return !(hour && minute && second);
		case 'object':
			return value != compareValue?.value;
		default:
			return value != compareValue;
	}
}
